import React from 'react';
import { Box, Typography, Button } from '@mui/material';

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
  errorStack: string;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '', errorStack: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error?.message || 'Unknown error',
      errorStack: error?.stack || '',
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('App crashed:', error, info.componentStack);
  }

  handleReload = () => {
    window.location.href = '/dashboard';
  };

  handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/landing';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="100vh"
          p={3}
          textAlign="center"
        >
          <Typography variant="h5" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            The app encountered an unexpected error.
          </Typography>
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'rgba(255,0,0,0.05)',
              border: '1px solid rgba(255,0,0,0.2)',
              borderRadius: 1,
              maxWidth: '90vw',
              maxHeight: '200px',
              overflow: 'auto',
              textAlign: 'left',
            }}
          >
            <Typography variant="body2" sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'error.main', wordBreak: 'break-word' }}>
              {this.state.errorMessage}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button variant="contained" onClick={this.handleReload}>
              Reload App
            </Button>
            <Button variant="outlined" onClick={this.handleLogout}>
              Sign Out
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
